import { SelectOptionType } from '@common/enums/global.enums'
import { Company } from '@common/types/company.types'
import { Option } from '@common/types/global.types'
import {
	getCompanyLabel,
	getCompanyNameAndCountry,
	getCompanyParentLabel
} from '@common/utils/company.utils'
import {
	GREY_COLORS,
	RED_COLORS
} from '@config/theme/colors'
import {
	Autocomplete,
	Box,
	createFilterOptions,
	Stack,
	styled,
	TextField,
	Typography
} from '@mui/material'
import { useAuth } from '@services/contexts/AuthContext'
import { useMemo } from 'react'
import {
	FieldValues,
	useController,
	UseControllerProps
} from 'react-hook-form'
import { useIntl } from 'react-intl'

interface CompanyAutocompleteProps<T extends FieldValues> extends UseControllerProps<T> {
	label?: string
	placeholder?: string
	onChange?: (option: Option | null) => void
	companies?: Company[]
}

/**
 * Company Autocomplete
 * @param control
 * @param name
 * @param label
 * @param placeholder
 * @param onChange
 * @param companies
 * @constructor
 */
const CompanyAutocomplete = <T extends FieldValues>(
	{
		control,
		name,
		label,
		placeholder,
		onChange,
		companies
	}: CompanyAutocompleteProps<T>
) => {
	const { formatMessage } = useIntl()
	const { isAdmin } = useAuth()

	const { field } = useController({
		control,
		name
	})

	const borderColor = field.value ? `1px solid ${RED_COLORS[200]}` : 'auto'

	const options = useMemo((): Option[] => (
		companies?.map((c) => ({
			type: SelectOptionType.OPTION,
			label: getCompanyLabel(c, formatMessage, isAdmin),
			value: `${c.id}`
		})) ?? []
	), [companies, isAdmin])

	const companiesByValue = useMemo(() => (
		new Map((companies ?? []).map((c) => [`${c.id}`, c]))
	), [companies])

	const filterOptions = useMemo(() => createFilterOptions<Option>({
		stringify: (option) => {
			const company = companiesByValue.get(`${option.value}`)

			return company ? getCompanyNameAndCountry(company, formatMessage) : option.label
		}
	}), [companiesByValue])

	const selectedOption = options.find((opt) => opt.value === field.value) || null

	return (
		<Container>
			{label && (
				<Typography>
					{label}
				</Typography>
			)}
			<Autocomplete<Option>
				value={selectedOption}
				options={options}
				filterOptions={filterOptions}
				getOptionKey={(option) => option.value}
				isOptionEqualToValue={(option, value) => option.value === value.value}
				onChange={(_, newValue) => {
					field.onChange(newValue?.value ?? '')
					onChange?.(newValue ?? null)
				}}
				renderOption={(props, option) => {
					const { key, ...optionProps } = props
					const company = companiesByValue.get(`${option.value}`)
					const parentLabel = company && getCompanyParentLabel(company, formatMessage, isAdmin)

					return (
						<Box key={key} component="li" {...optionProps}>
							<OptionLabel>
								{company ? getCompanyNameAndCountry(company, formatMessage) : option.label}
								{parentLabel && <ParentLabel>{parentLabel}</ParentLabel>}
							</OptionLabel>
						</Box>
					)
				}}
				renderInput={(params) => (
					<TextField
						{...params}
						placeholder={placeholder}
						variant="outlined"
						size="small"
						sx={{
							'& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline': {
								border: borderColor
							},
							'&:hover .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline': {
								border: borderColor
							},
							'& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
								border: borderColor
							}
						}}
					/>
				)}
				noOptionsText={formatMessage({ id: 'dashboard.trainees.filters.companiesNotFound' })}
			/>
		</Container>
	)
}

const Container = styled(Stack)({
	flex: 1
})

// Single flex item, so the company and its parent share one text flow instead of being laid out side by side
const OptionLabel = styled('span')({
	minWidth: 0
})

const ParentLabel = styled('span')({
	color: GREY_COLORS[500]
})

export default CompanyAutocomplete
